import React, { useState, useEffect } from 'react';
import { auth, db } from './firebase';
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, onSnapshot } from 'firebase/firestore'; 
import MainApp from './MainApp';

export default function App() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null); 
  const [loading, setLoading] = useState(true);
  const [isLoginView, setIsLoginView] = useState(true);

  // Monitor Authentication State & Fetch Profile (Safely)
  useEffect(() => {
    let unsubscribeProfile = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        // onSnapshot handles offline cache and minor network dips gracefully without crashing
        unsubscribeProfile = onSnapshot(
          doc(db, "users", currentUser.uid), 
          (docSnap) => {
            if (docSnap.exists()) {
              setUserData(docSnap.data());
            }
            setLoading(false);
          },
          (error) => {
            console.error("Profile fetch error:", error);
            // Even if it fails, stop the loading spinner so it doesn't hang
            setLoading(false); 
          }
        );
      } else {
        setUserData(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeProfile) unsubscribeProfile();
    };
  }, []);

 const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const email = e.target.email.value;
      const password = e.target.password.value;
      const userCred = await createUserWithEmailAndPassword(auth, email, password);
      
      // Calculate the date exactly 15 days from right now
      const trialEndDate = new Date();
      trialEndDate.setDate(trialEndDate.getDate() + 15);

      const newProfile = {
        email: email,
        isPremium: false,           
        trialEndsAt: trialEndDate,  
        setupConfig: {} 
      };

      // Save to database
      await setDoc(doc(db, "users", userCred.user.uid), newProfile);
      
      // FORCE THE UI TO UPDATE INSTANTLY (Fixes the hanging screen)
      setUserData(newProfile);
      
    } catch (error) {
      alert("Registration failed: " + error.message);
    }
  };
  

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await signInWithEmailAndPassword(auth, e.target.email.value, e.target.password.value);
    } catch (error) {
      alert("Login failed: " + error.message);
    }
  };

  // --- THE LICENSE REDEMPTION ENGINE ---
  const handleRedeemKey = async (e) => {
    e.preventDefault();
    const keyInput = e.target.licenseKey.value.trim().toUpperCase();
    if (!keyInput) return;

    try {
      // 1. Check if the key exists in the 'license_keys' vault
      const keyRef = doc(db, "license_keys", keyInput);
      const keySnap = await getDoc(keyRef);

      if (!keySnap.exists()) {
        return alert("❌ Invalid License Key. Please check for typos.");
      }

      const keyData = keySnap.data();

      // 2. Check if the key has already been used
      if (keyData.status === "used") {
        return alert("❌ This License Key has already been redeemed.");
      }

      // 3. Burn the key and upgrade the user
      await updateDoc(keyRef, {
        status: "used",
        usedBy: user.email,
        redeemedAt: new Date()
      });

      await updateDoc(doc(db, "users", user.uid), {
        isPremium: true
      });

      alert("✅ Activation Successful! Welcome to GNEWS Class Record Premium.");

    } catch (error) {
      alert("Error verifying key: " + error.message);
    }
  };

  if (loading) return <div style={containerStyle}>Loading GNEWS Class Record...</div>;

  if (!user) {
    return (
      <div style={containerStyle}>
        <h2 style={{ color: '#0047a0', textAlign: 'center', marginBottom: '20px' }}>GNEWS Class Record</h2>
        <div style={{ backgroundColor: '#fff', padding: '25px', borderRadius: '10px', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}>
          
          {isLoginView ? (
            <>
              <h3 style={{ marginTop: 0, color: '#333', textAlign: 'center' }}>Teacher Log In</h3>
              <form onSubmit={handleLogin} style={{ marginBottom: '10px' }}>
                <input name="email" type="email" placeholder="Email Address" style={inputStyle} required />
                <input name="password" type="password" placeholder="Password" style={inputStyle} required />
                <button type="submit" style={btnStyle}>Log In</button>
              </form>
              
              <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '15px', color: '#666' }}>
                No account yet? <span onClick={() => setIsLoginView(false)} style={{ color: '#0047a0', cursor: 'pointer', fontWeight: 'bold', textDecoration: 'underline' }}>Register here</span>
              </p>
            </>
          ) : (
            <>
              <h3 style={{ marginTop: 0, color: '#333', textAlign: 'center' }}>Create Account</h3>
              <form onSubmit={handleRegister} style={{ marginBottom: '10px' }}>
                <input name="email" type="email" placeholder="New Email Address" style={inputStyle} required />
                <input name="password" type="password" placeholder="Create Password (min 6 chars)" style={inputStyle} required />
                <button type="submit" style={{ ...btnStyle, backgroundColor: '#27ae60' }}>Register Account</button>
              </form>

              <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '15px', color: '#666' }}>
                Already have an account? <span onClick={() => setIsLoginView(true)} style={{ color: '#0047a0', cursor: 'pointer', fontWeight: 'bold', textDecoration: 'underline' }}>Log In here</span>
              </p>
            </>
          )}

        </div>
      </div>
    );
  }

  // Ensure userData is loaded before checking trial status
  if (user && !userData) {
    return <div style={containerStyle}>Loading your profile...</div>;
  }

  // --- THE GATEKEEPER LOGIC ---
  if (user && userData) {
    const now = new Date();
    // Convert Firestore timestamp to a Javascript Date object 
    const trialEnd = userData.trialEndsAt?.toDate ? userData.trialEndsAt.toDate() : (userData.trialEndsAt instanceof Date ? userData.trialEndsAt : new Date(0));
    const isTrialExpired = now > trialEnd;

    // If they are not premium AND their trial is over, show the Paywall
    if (!userData.isPremium && isTrialExpired) {
      return (
        <div style={containerStyle}>
          <div style={{ backgroundColor: '#fff', padding: '30px', borderRadius: '10px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', textAlign: 'center' }}>
            <span style={{ fontSize: '50px' }}>🔒</span>
            <h2 style={{ color: '#e74c3c', marginTop: '10px' }}>Trial Expired</h2>
            <p style={{ color: '#555', fontSize: '14px', lineHeight: '1.5', marginBottom: '20px' }}>
              Your 15-day free trial has concluded. To continue accessing your class records, grades, and reports, please activate your account.
            </p>
            
            <div style={{ backgroundColor: '#f9f9f9', padding: '15px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '20px' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#0047a0' }}>Have a License Key?</h4>
              <form onSubmit={handleRedeemKey}>
                <input name="licenseKey" type="text" placeholder="e.g., GNEWS-ABCD-1234" style={{...inputStyle, textAlign: 'center', letterSpacing: '1px', textTransform: 'uppercase'}} required />
                <button type="submit" style={{...btnStyle, backgroundColor: '#27ae60'}}>Unlock App Now</button>
              </form>
            </div>

            <p style={{ fontSize: '12px', color: '#888' }}>
              Need a key? Contact your authorized agent or text [Your Phone Number] to purchase a lifetime license via GCash.
            </p>
            <button onClick={() => signOut(auth)} style={{ background: 'none', border: 'none', color: '#0047a0', textDecoration: 'underline', marginTop: '15px', cursor: 'pointer', fontWeight: 'bold' }}>Log Out</button>
          </div>
        </div>
      );
    }

    // If they are Premium OR Trial is still active, load the main app
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: '#e0e0e0' }}>
        <MainApp user={user} isPWA={userData.isPWA === true} handleLogout={() => signOut(auth)} />
      </div>
    );
  }
}

const containerStyle = { padding: '20px', fontFamily: 'Arial, sans-serif', maxWidth: '400px', margin: '40px auto', borderRadius: '10px' };
const inputStyle = { width: '100%', padding: '12px', marginBottom: '15px', boxSizing: 'border-box', fontSize: '14px', borderRadius: '6px', border: '1px solid #ccc', backgroundColor: '#f9f9f9' };
const btnStyle = { width: '100%', padding: '14px', backgroundColor: '#0047a0', color: 'white', border: 'none', borderRadius: '6px', fontSize: '16px', cursor: 'pointer', fontWeight: 'bold', transition: '0.2s' };